// https://www.figma.com/file/q7KlcLyYFswO2QB3I4BWmE/Book-list-management%2C-profiles-%2C-IA?node-id=615%3A975
import { useEffect } from 'react'
import { css, useTheme } from '@fable/theme'
import { Logo, TransitionElement } from '@fable/components'
import isEmpty from 'lodash/isEmpty'
import { useActions, useSidebar, useTypedSelector } from '../../../../hooks'
import Sidebar from '../../../../components/sidebar/Sidebar'
import BooklistManage from '../../../../components/book_list_manage/BooklistManage'
import BooklistPicker from '../../../../components/book_list_picker/BooklistPicker'
import { FlexBox } from '@fable/components'
import {
  BOOK_LIST_SAVE,
  BOOK_LIST_CREATE,
  BOOK_LIST_EDIT,
} from '../../../store/constants'
import { SidebarButtons } from '../../../../components/sidebar/SidebarButtons'

const BookListDetailSidebar = () => {
  const { mediaQueries } = useTheme()
  const { onOpenSidebar, onCloseSidebar, initialContent, sidebarData } =
    useSidebar()
  const {
    modifyBooklists,
    addBookList,
    editBookList,
    createBookListClear,
    prependBookList,
  } = useActions()
  const { createdBookList, modifyBooklistsState, bookList } = useTypedSelector(
    ({ books }) => books
  )
  const loading = modifyBooklistsState.loading
  const isBooklistCreate = initialContent === BOOK_LIST_CREATE
  const isBooklistEdit = initialContent === BOOK_LIST_EDIT

  const handleClickSave = async () => {
    if (isBooklistCreate) {
      await addBookList()
      await prependBookList()

      if (sidebarData) {
        onOpenSidebar(BOOK_LIST_SAVE)
      } else {
        onCloseSidebar()
      }
    } else if (isBooklistEdit) {
      await editBookList(bookList.data?.id, createdBookList)
      onCloseSidebar()
    } else {
      onCloseSidebar()
      modifyBooklists()
    }
  }

  const handleClickCancel = () => {
    if (isBooklistCreate) {
      onOpenSidebar(BOOK_LIST_SAVE)
    } else {
      onCloseSidebar()
    }
  }

  const titleForCreate = 'Create a new list'
  const titleForEdit = 'Edit list'
  const titleForPicker = 'Save to a list'
  const buttons = [
    {
      label: 'Save',
      onClick: handleClickSave,
      loading,
      disabled: !createdBookList?.name && initialContent !== BOOK_LIST_SAVE,
    },
    { label: 'Cancel', secondary: true, onClick: handleClickCancel },
  ]

  const sidebarButtons = (
    <SidebarButtons
      className={css`
        padding-bottom: 150px;
        ${mediaQueries.tabletL} {
          padding-bottom: 40px;
        }
      `}
      buttons={buttons}
      attached
    />
  )

  useEffect(() => {
    /*  BooklistCreate is rendered on top of BooklistPicker without unmounting in order
    to animate in/out, so data must be cleared here */
    if (!isEmpty(createdBookList) && !isBooklistCreate && !isBooklistEdit) {
      createBookListClear()
    }
  }, [createBookListClear, createdBookList, isBooklistCreate, isBooklistEdit])

  const getTitle = () => {
    if (isBooklistCreate) {
      return titleForCreate
    }

    if (isBooklistEdit) {
      return titleForEdit
    }

    return titleForPicker
  }

  return (
    <Sidebar title={getTitle()}>
      {loading ? (
        <FlexBox
          centerAll
          className={css`
            height: 100%;
            width: 100vw;
            max-width: 512px;
          `}
        >
          <Logo animate isIcon />
        </FlexBox>
      ) : (
        <div
          className={css`
            position: relative;
            width: 100vw;
            max-width: 512px;
          `}
        >
          <TransitionElement
            className={css`
              position: absolute;
              top: 0;
              left: 0;
              opacity: 0;
              transform: translateX(100%);
              z-index: 1;
            `}
            transitionType={
              isBooklistCreate || isBooklistEdit
                ? 'slideInFromRight'
                : 'slideOutFromLeft'
            }
          >
            <BooklistManage mode={isBooklistEdit ? 'edit' : 'create'} />
            {sidebarButtons}
          </TransitionElement>
          {!!initialContent &&
          !modifyBooklistsState.loading &&
          !isBooklistEdit ? (
            <TransitionElement
              transitionType={isBooklistCreate ? 'fadeOut' : 'fadeIn'}
              className={css`
                display: ${isBooklistCreate ? 'none' : 'static'};
              `}
            >
              {sidebarData && (
                <>
                  <BooklistPicker
                    book={sidebarData.book}
                    bookId={sidebarData.bookId}
                  />
                  {sidebarButtons}
                </>
              )}
            </TransitionElement>
          ) : null}
        </div>
      )}
    </Sidebar>
  )
}

export default BookListDetailSidebar
